import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSmartViewsByCategory, buildSmartViewUrl, categoryInfo, type SmartView } from '../utils/smartViews'
import styles from './SmartViewLibrary.module.css'

type SmartViewLibraryProps = {
  isOpen: boolean
  onClose: () => void
}

export function SmartViewLibrary({ isOpen, onClose }: SmartViewLibraryProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null)

  if (!isOpen) return null

  function handleViewClick(view: SmartView) {
    setSelectedViewId(view.id)
    navigate(buildSmartViewUrl(view))
    onClose()
  }

  const categories: Array<SmartView['category']> = ['athletes', 'deals', 'contacts']

  // Filter views based on search query
  const filteredCategories = categories.map(category => ({
    category,
    views: getSmartViewsByCategory(category).filter(view =>
      view.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      view.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(c => c.views.length > 0)

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>Smart View Library</h2>
            <p className={styles.subtitle}>Curated data filters and saved views.</p>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={styles.searchWrapper}>
          <div className={styles.searchContainer}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search presets..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.content}>
          {filteredCategories.map(({ category, views }) => {
            const info = categoryInfo[category]

            return (
              <div key={category} className={styles.category}>
                <div className={styles.categoryHeader}>
                  <span>{info.label.toUpperCase()}</span>
                </div>
                <div className={styles.viewList}>
                  {views.map(view => (
                    <button
                      key={view.id}
                      className={`${styles.viewItem} ${selectedViewId === view.id ? styles.selected : ''}`}
                      onClick={() => handleViewClick(view)}
                    >
                      <span className={styles.viewIcon}>
                        {getViewIcon(view.id)}
                      </span>
                      <span className={styles.viewContent}>
                        <span className={styles.viewName}>{view.name}</span>
                        {view.description && (
                          <span className={styles.viewDescription}>{view.description}</span>
                        )}
                      </span>
                      <span className={styles.checkmark}>
                        <CheckIcon />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}

          {filteredCategories.length === 0 && (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-tertiary)' }}>
              No views match your search
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.createButton}>
            <PlusIcon />
            Create New View
          </button>
          <div className={styles.footerLinks}>
            <button className={styles.footerLink}>Library Settings</button>
            <span className={styles.footerDivider}>·</span>
            <button className={styles.footerLink}>Export Presets</button>
          </div>
        </div>
      </div>
    </>
  )
}

// Icon components
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  )
}

function getViewIcon(viewId: string) {
  switch (viewId) {
    case 'my-athletes':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      )
    case 'expiring-soon':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      )
    case 'top-earners':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      )
    case 'closing-this-month':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    case 'stalled':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="10" y1="15" x2="10" y2="9" />
          <line x1="14" y1="15" x2="14" y2="9" />
        </svg>
      )
    case 'high-value':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )
    case 'recent-interactions':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l4 2" />
        </svg>
      )
    case 'decision-makers':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      )
    default:
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      )
  }
}

export default SmartViewLibrary
